import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import type { DrivingCourseOption } from '../data/courseOptions';
import type { DrivingAssists } from '../model/drivingPreferences';
import {
  formatLapTime,
  type DrivingSessionState,
} from '../model/drivingSession';
import {
  getEstimatedRangeKm,
  getTripConsumptionWhPerKm,
  type VehicleState,
} from '../model/vehicleDriving';
import { getSurfaceName } from '../model/drivingSurface';
import {
  formatChallengeValue,
  type DrivingChallengeState,
} from '../model/drivingChallenges';
import type { DrivingChallengeOption } from '../data/challengeOptions';
import type { FrameStatsValue } from '../three/FrameStats';
import type { DrivingTelemetryState } from '../model/drivingTelemetry';
import type { Powertrain } from '../types';
import { buildClassName } from './buildClassName';
import { DrivingTelemetryChart } from './DrivingTelemetryChart';

/** 내연기관 계기판의 레드라인. 가상 6단 변속의 회전수 범위와 맞춰 둡니다. */
const ICE_REDLINE_RPM = 7000;

interface HudSnapshot {
  x: number;
  z: number;
  heading: number;
  speedKmh: number;
  gear: VehicleState['gear'];
  rpm: number;
  powerKw: number;
  batteryPercent: number | null;
  remainingKWh: number | null;
  consumptionWhPerKm: number | null;
  estimatedRangeKm: number | null;
  consumedKWh: number | null;
  recoveredKWh: number | null;
  distanceKm: number | null;
  gradePercent: number;
  surfaceName: string;
  slipPercent: number;
  absActive: boolean;
  tcsActive: boolean;
  escActive: boolean;
  currentTime: number;
  bestTime: number | null;
  lastTime: number | null;
  checkpoint: number;
  completedLaps: number;
  runFinished: boolean;
  topSpeedKmh: number;
  braking: DrivingSessionState['lastBraking'];
  challengeStatus: DrivingChallengeState['status'];
  challengeMessage: string;
  challengeResult: number | null;
  challengeBest: number | null;
  roadImpactPercent: number;
  fps: number;
  telemetryRevision: number;
}

function snapshotOf(
  vehicle: VehicleState,
  session: DrivingSessionState,
  challenge: DrivingChallengeState,
  stats: FrameStatsValue,
  telemetry: DrivingTelemetryState
): HudSnapshot {
  const energy = vehicle.energy;
  const consumption = energy === null ? null : getTripConsumptionWhPerKm(energy);
  return {
    x: Math.round(vehicle.x * 2) / 2,
    z: Math.round(vehicle.z * 2) / 2,
    heading: Math.round((vehicle.heading * 180) / Math.PI),
    speedKmh: Math.round(Math.abs(vehicle.speed) * 3.6),
    gear: vehicle.gear,
    rpm: Math.round(vehicle.rpm / 50) * 50,
    // 출력도 회전수처럼 눈금을 끊어 매 프레임 다시 렌더되지 않게 합니다.
    powerKw: Math.round(vehicle.powerKw / 5) * 5,
    batteryPercent:
      energy === null ? null : Math.round((energy.remainingKWh / energy.usableBatteryKWh) * 1000) / 10,
    remainingKWh: energy === null ? null : Math.round(energy.remainingKWh * 10) / 10,
    consumptionWhPerKm: consumption === null ? null : Math.round(consumption),
    estimatedRangeKm: energy === null ? null : Math.round(getEstimatedRangeKm(energy)),
    consumedKWh: energy === null ? null : Math.round(energy.consumedKWh * 100) / 100,
    recoveredKWh: energy === null ? null : Math.round(energy.recoveredKWh * 100) / 100,
    distanceKm: energy === null ? null : Math.round(energy.distanceKm * 100) / 100,
    gradePercent: Math.round(vehicle.gradePercent * 10) / 10,
    surfaceName: getSurfaceName(vehicle.surfaceType),
    slipPercent: Math.round(vehicle.slipIntensity * 100),
    absActive: vehicle.absActive,
    tcsActive: vehicle.tcsActive,
    escActive: vehicle.escActive,
    currentTime: Math.round(session.currentTimeSeconds * 100) / 100,
    bestTime: session.bestTimeSeconds,
    lastTime: session.lastTimeSeconds,
    checkpoint: session.nextCheckpointIndex,
    completedLaps: session.completedLaps,
    runFinished: session.runFinished,
    topSpeedKmh: Math.round(session.topSpeedKmh),
    braking: session.lastBraking,
    challengeStatus: challenge.status,
    challengeMessage: challenge.message,
    challengeResult: challenge.resultValue,
    challengeBest: challenge.bestValue,
    // 노면과 오브젝트 충격 중 큰 값을 10% 단위로 끊어 HUD 재렌더를 제한합니다.
    roadImpactPercent: Math.round(Math.max(vehicle.roadImpact, vehicle.collision.impact) * 10) * 10,
    fps: stats.fps,
    telemetryRevision: telemetry.revision,
  };
}

function snapshotsEqual(first: HudSnapshot, second: HudSnapshot): boolean {
  return JSON.stringify(first) === JSON.stringify(second);
}

function DrivingMinimap({
  course,
  snapshot,
}: {
  course: DrivingCourseOption;
  snapshot: HudSnapshot;
}) {
  const map = useMemo(() => {
    const { minX, maxX, minZ, maxZ } = course.mapBounds;
    const width = Math.max(1, maxX - minX);
    const height = Math.max(1, maxZ - minZ);
    // X/Z에 같은 축척을 써야 원형 트랙이 타원으로 찌그러지지 않습니다.
    const scale = 88 / Math.max(width, height);
    const centerX = (minX + maxX) / 2;
    const centerZ = (minZ + maxZ) / 2;
    const routeStep = Math.max(1, Math.ceil(course.route.points.length / 180));
    const routePoints = course.route.points
      .filter((_, index) => index % routeStep === 0)
      .map(
        (point) =>
          `${(50 + (point.x - centerX) * scale).toFixed(2)},${(
            50 -
            (point.z - centerZ) * scale
          ).toFixed(2)}`
      );
    if (course.route.closed && routePoints.length > 0) {
      routePoints.push(routePoints[0]);
    }
    return { scale, centerX, centerZ, routePoints: routePoints.join(' ') };
  }, [course]);
  const toX = (x: number) => 50 + (x - map.centerX) * map.scale;
  const toY = (z: number) => 50 - (z - map.centerZ) * map.scale;
  const carX = toX(snapshot.x);
  const carY = toY(snapshot.z);
  const directionLength = 5;
  const directionX = carX + Math.cos((snapshot.heading * Math.PI) / 180) * directionLength;
  const directionY = carY + Math.sin((snapshot.heading * Math.PI) / 180) * directionLength;

  return (
    <svg
      className="driving-minimap"
      viewBox="0 0 100 100"
      role="img"
      aria-label={`${course.name} 미니맵, 체크포인트 ${snapshot.checkpoint + 1}`}
    >
      <rect x="1" y="1" width="98" height="98" rx="12" className="driving-minimap__background" />
      <polyline points={map.routePoints} className="driving-minimap__course" />
      {course.checkpoints.map((checkpoint, index) => (
        <circle
          key={checkpoint.id}
          cx={toX(checkpoint.x)}
          cy={toY(checkpoint.z)}
          r={index === snapshot.checkpoint ? 2.7 : 1.7}
          className={buildClassName(
            'driving-minimap__checkpoint',
            'driving-minimap__checkpoint--next',
            index === snapshot.checkpoint
          )}
        />
      ))}
      <line
        x1={carX}
        y1={carY}
        x2={directionX}
        y2={directionY}
        className="driving-minimap__direction"
      />
      <circle cx={carX} cy={carY} r="2.4" className="driving-minimap__vehicle" />
    </svg>
  );
}

function AssistLamp({
  label,
  enabled,
  active,
}: {
  label: string;
  enabled: boolean;
  active: boolean;
}) {
  return (
    <span
      className={buildClassName(
        buildClassName('driving-hud__assist', 'driving-hud__assist--enabled', enabled),
        'driving-hud__assist--active',
        active
      )}
    >
      {label}
    </span>
  );
}

interface GaugeReadout {
  label: string;
  value: string;
  /** 0~1 막대 채움 비율. */
  fillRatio: number;
  regenerating: boolean;
}

/**
 * 파워트레인에 맞는 계기 하나를 고릅니다.
 *
 * 전기차에 회전수 게이지를 달지 않는 이유: 단수도 레드라인도 운전자가 할 일이 없어서
 * 양산 전기차는 타코미터 대신 출력·회생 게이지를 씁니다. 모터 회전수 자체는 존재하지만
 * (VehicleState.rpm에 있습니다) 계기판에 띄울 이유가 없는 값입니다.
 */
function resolveGauge(
  powertrain: Powertrain,
  peakPowerKw: number,
  regenLimitKw: number,
  snapshot: HudSnapshot
): GaugeReadout {
  if (powertrain !== 'ev') {
    return {
      label: 'RPM',
      value: snapshot.rpm.toLocaleString(),
      fillRatio: Math.min(1, snapshot.rpm / ICE_REDLINE_RPM),
      regenerating: false,
    };
  }

  const regenerating = snapshot.powerKw < 0;
  return {
    label: regenerating ? '회생' : '출력',
    value: `${Math.abs(snapshot.powerKw).toLocaleString()} kW`,
    fillRatio: Math.min(
      1,
      Math.abs(snapshot.powerKw) / Math.max(1, regenerating ? regenLimitKw : peakPowerKw)
    ),
    regenerating,
  };
}

export interface DrivingHudProps {
  vehicleStateRef: RefObject<VehicleState>;
  sessionRef: RefObject<DrivingSessionState>;
  challengeStateRef: RefObject<DrivingChallengeState>;
  telemetryStateRef: RefObject<DrivingTelemetryState>;
  frameStatsRef: RefObject<FrameStatsValue>;
  /** 선택한 차량의 구동 방식. 회전수 계기와 출력 계기를 가릅니다. */
  powertrain: Powertrain;
  /** 출력 게이지의 만눈금 (kW). 전기 구동에서만 사용합니다. */
  peakPowerKw: number;
  /** 회생 게이지의 만눈금 (kW). */
  regenLimitKw: number;
  course: DrivingCourseOption;
  challenge: DrivingChallengeOption;
  assists: DrivingAssists;
  /** 연결된 게임패드 이름. 없으면 표시하지 않습니다. */
  gamepadName?: string | null;
}

/** 하나의 rAF 구독으로 차량 텔레메트리·랩·미니맵을 함께 갱신합니다. */
export function DrivingHud({
  vehicleStateRef,
  sessionRef,
  challengeStateRef,
  telemetryStateRef,
  frameStatsRef,
  powertrain,
  peakPowerKw,
  regenLimitKw,
  course,
  challenge,
  assists,
  gamepadName,
}: DrivingHudProps) {
  const readSnapshot = useCallback(
    () =>
      snapshotOf(
        vehicleStateRef.current,
        sessionRef.current,
        challengeStateRef.current,
        frameStatsRef.current,
        telemetryStateRef.current
      ),
    [vehicleStateRef, sessionRef, challengeStateRef, frameStatsRef, telemetryStateRef]
  );
  const [snapshot, setSnapshot] = useState(readSnapshot);

  useEffect(() => {
    let frame = 0;
    let shown = readSnapshot();
    setSnapshot(shown);
    const tick = () => {
      const next = readSnapshot();
      if (!snapshotsEqual(shown, next)) {
        shown = next;
        setSnapshot(next);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [readSnapshot, course.id, challenge.id]);

  const checkpointTotal = course.checkpoints.length;
  const checkpointShown = Math.min(snapshot.checkpoint + 1, checkpointTotal);
  const braking = snapshot.braking;
  const gauge = resolveGauge(powertrain, peakPowerKw, regenLimitKw, snapshot);

  return (
    <div className="driving-hud" aria-label="주행 정보">
      <section className="driving-hud__telemetry" aria-label="차량 텔레메트리">
        <div className="driving-hud__primary">
          <strong>{snapshot.speedKmh}</strong>
          <span>km/h</span>
          <b>{snapshot.gear}</b>
        </div>
        {/*
          막대는 아래 dt/dd와 같은 값을 그림으로 반복합니다. aria-label을 달면 스크린
          리더가 같은 숫자를 두 번 읽으므로 그림만 남깁니다.
        */}
        <div
          className={buildClassName(
            'driving-hud__meter',
            'driving-hud__meter--regen',
            gauge.regenerating
          )}
          aria-hidden="true"
        >
          <span style={{ width: `${gauge.fillRatio * 100}%` }} />
        </div>
        <dl className="driving-hud__values">
          <div><dt>{gauge.label}</dt><dd>{gauge.value}</dd></div>
          <div><dt>경사</dt><dd>{snapshot.gradePercent > 0 ? '+' : ''}{snapshot.gradePercent}%</dd></div>
          <div><dt>노면</dt><dd>{snapshot.surfaceName}</dd></div>
          <div><dt>슬립</dt><dd>{snapshot.slipPercent}%</dd></div>
          <div><dt>충격</dt><dd>{snapshot.roadImpactPercent}%</dd></div>
          <div><dt>FPS</dt><dd>{snapshot.fps}</dd></div>
        </dl>
        {snapshot.batteryPercent === null ? null : (
          <dl className="driving-hud__energy" aria-label="전기차 에너지 정보">
            <div><dt>배터리</dt><dd>{snapshot.batteryPercent}%</dd></div>
            <div><dt>잔량</dt><dd>{snapshot.remainingKWh} kWh</dd></div>
            <div><dt>예상 거리</dt><dd>{snapshot.estimatedRangeKm} km</dd></div>
            <div>
              <dt>누적 전비</dt>
              <dd>
                {snapshot.consumptionWhPerKm === null
                  ? '-- Wh/km'
                  : `${snapshot.consumptionWhPerKm} Wh/km`}
              </dd>
            </div>
            <div><dt>사용</dt><dd>{snapshot.consumedKWh} kWh</dd></div>
            <div><dt>회수</dt><dd>{snapshot.recoveredKWh} kWh</dd></div>
            <div><dt>주행</dt><dd>{snapshot.distanceKm} km</dd></div>
          </dl>
        )}
        <div className="driving-hud__assists" aria-label="주행 보조 상태">
          <AssistLamp label="ABS" enabled={assists.abs} active={snapshot.absActive} />
          <AssistLamp label="TCS" enabled={assists.tcs} active={snapshot.tcsActive} />
          <AssistLamp label="ESC" enabled={assists.esc} active={snapshot.escActive} />
        </div>
        {gamepadName ? (
          <p className="driving-hud__gamepad" title={gamepadName}>게임패드 연결됨</p>
        ) : null}
      </section>

      <section className="driving-hud__timing" aria-label="코스 기록">
        <div className="driving-hud__map-wrap">
          <DrivingMinimap course={course} snapshot={snapshot} />
          <div>
            <strong>{course.shortName}</strong>
            <span>
              {snapshot.runFinished ? 'FINISH' : `CP ${checkpointShown}/${checkpointTotal}`}
            </span>
          </div>
        </div>
        {challenge.id === 'free-drive' ? null : (
          <div
            className={buildClassName(
              'driving-hud__challenge',
              'driving-hud__challenge--done',
              snapshot.challengeStatus === 'complete'
            )}
          >
            {/*
              메시지는 km/h마다 바뀌는 텔레메트리입니다. 여기에 live region을 걸면
              스크린 리더가 끝없이 읽습니다. 상태 전환만 아래 별도 영역에서 알립니다.
            */}
            <strong>{challenge.name}</strong>
            <span aria-hidden="true">{snapshot.challengeMessage}</span>
            <span className="visually-hidden" role="status">
              {snapshot.challengeStatus === 'complete'
                ? `${challenge.name} 완료`
                : snapshot.challengeStatus === 'invalid'
                  ? `${challenge.name} 무효`
                  : snapshot.challengeStatus === 'running'
                    ? `${challenge.name} 측정 중`
                    : `${challenge.name} 대기 중`}
            </span>
            <small>
              기록 {formatChallengeValue(challenge.id, snapshot.challengeResult)} · 베스트{' '}
              {formatChallengeValue(challenge.id, snapshot.challengeBest)}
            </small>
          </div>
        )}
        <dl className="driving-hud__times">
          <div><dt>{snapshot.runFinished ? '완주' : '현재'}</dt><dd>{formatLapTime(snapshot.runFinished ? snapshot.lastTime : snapshot.currentTime)}</dd></div>
          <div><dt>최근</dt><dd>{formatLapTime(snapshot.lastTime)}</dd></div>
          <div><dt>베스트</dt><dd>{formatLapTime(snapshot.bestTime)}</dd></div>
          <div><dt>{course.kind === 'circuit' ? '랩' : '완주'}</dt><dd>{snapshot.completedLaps}</dd></div>
          <div><dt>최고속도</dt><dd>{snapshot.topSpeedKmh} km/h</dd></div>
          {braking ? (
            <div className="driving-hud__braking">
              <dt>최근 제동</dt>
              <dd>
                {Math.round(braking.startSpeedKmh)}→{Math.round(braking.endSpeedKmh)} km/h ·{' '}
                {braking.distanceMetres.toFixed(1)}m · {braking.elapsedSeconds.toFixed(1)}s
              </dd>
            </div>
          ) : null}
        </dl>
        {telemetryStateRef.current.completedRun === null ? null : (
          <DrivingTelemetryChart run={telemetryStateRef.current.completedRun} />
        )}
      </section>
    </div>
  );
}
